import type { uuid } from "@/interfaces";
import type { PropertyPriceRow, PropertyPriceSetting } from "@/repositories/vendor-property-service-pricing.repository";

export type PricingFormEntry = { price: number; payment: number; isDefault: boolean };

export type PricedService = { id: uuid; parent: uuid | null; price: number | null; payment: number | null };

export function propertyPricesToSave(services: readonly PricedService[], form: Readonly<Record<string, PricingFormEntry>>, saved: readonly PropertyPriceRow[]): PropertyPriceSetting[] {
  return services
    .filter((service) => service.parent !== null)
    .flatMap((service) => {
      const existing = saved.find((row) => row.service === service.id);
      const entry = form[service.id];
      if (entry === undefined) return existing === undefined ? [] : [{ service: service.id, price: existing.price, payment: existing.payment }];
      if (entry.isDefault) return [];
      const differsFromDefault = service.price !== entry.price || service.payment !== entry.payment;
      return existing !== undefined || differsFromDefault ? [{ service: service.id, price: entry.price, payment: entry.payment }] : [];
    });
}

export function changesSavedPrices(prices: readonly PropertyPriceSetting[], saved: readonly PropertyPriceRow[]): boolean {
  if (prices.length !== saved.length) return true;
  return prices.some((setting) => !saved.some((row) => row.service === setting.service && row.price === setting.price && row.payment === setting.payment));
}

export function pricingFormOf(services: readonly { id: uuid; price: number; payment: number }[], saved: readonly PropertyPriceRow[]): Record<string, PricingFormEntry> {
  return Object.fromEntries(
    services.map((service) => {
      const price = saved.find((row) => row.service === service.id);
      return [service.id, { price: price?.price ?? service.price, payment: price?.payment ?? service.payment, isDefault: price === undefined }];
    }),
  );
}
